import { DictionaryEntry } from "@/types/dictionaryApi";
import {
  AuthResponse,
  ChatMessage,
  ChatSession,
  CreateProjectResponse,
  DashboardChartsData,
  DashboardCountMetrics,
  FetchProjectsResponse,
  FetchProjectsResponseSupa,
  FormValues,
  Product,
  Project,
  ProjectsWithPagination,
  SharedProject,
  SharedProjectViewer,
  SignelUserResponse,
  SingleProjectResponse,
  SuggestedTopic,
  UpdateUserInput,
  UsageStats,
  User,
  Visibility,
} from "@/types/types";
import { authApi, aiApi } from "./axios";
import axios from "axios";
import { SERVER_API_PUBLIC } from "@/utils/constants";
import { prepareFormData } from "./ghostWriter.service";
import { getAuthTokens } from "@/store/useAuthStore";

export class ApiService {
  static async getUserWritingNiche() {
    const response = await aiApi.get(`/user/niche`);

    if (response.status != 200) {
      throw new Error("Failed to fetch user profile");
    }

    return response.data.data as string[];
  }

  static async fetchProjects({
    pageParam = 0,
    limit = 10,
  }: {
    pageParam?: number | null;
    limit?: number;
  }): Promise<FetchProjectsResponse> {
    const page = (pageParam ?? 0) / limit + 1;

    const response = await aiApi.get<ProjectsWithPagination>(
      `/projects?page=${page}&page_size=${limit}`
    );

    const { projects, page: currentPage, total_pages } = response.data;

    const hasMore = currentPage < total_pages;

    return {
      projects,
      nextCursor: hasMore ? (pageParam ?? 0) + limit : null,
    };
  }

  static async extractWritingPatterns(data: FormData) {
    const response = await aiApi.post(`/user/patterns/analyze`, data);
    return response.data.data;
  }

  static async updateUserWritingNiche(data: string[]) {
    const response = await aiApi.post(`/user/niche`, data);

    return response.data.data;
  }

  static async getsuggestedTopics(): Promise<SuggestedTopic[]> {
    const response = await aiApi.get(`/user/suggested-topics`);

    return response.data;
  }

  static async getSingleProject(
    project_id: string
  ): Promise<SingleProjectResponse> {
    const response = await aiApi.get(`/projects/${project_id}`);

    return response.data;
  }

  static async getUserProjects(): Promise<{
    page: number;
    projects: { id: string; name: string }[];
    total: number;
    total_pages: number;
  }> {
    const response = await aiApi.get(`/projects`);

    return response.data;
  }

  // suppose to collect userId
  static async deleteProject(projectId: string) {
    const response = await aiApi.delete(`/project?project_id=${projectId}`);
    if (response.status != 200) {
      throw new Error("unable to delete project");
    }

    return;
  }

  static async renameProject(project_id: string, name: string) {
    const reqBody = {
      project_id,
      name,
    };

    const response = await aiApi.patch("/project", reqBody);
    if (response.status != 200) {
      throw new Error("unable to update project");
    }

    return response.data;
  }

  static async fetchDictionaryWord(word: string): Promise<DictionaryEntry[]> {
    if (!word) {
      throw new Error("Word is required");
    }

    const response = await fetch(
      `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(
        word
      )}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Word not found");
      }
      throw new Error("Failed to fetch dictionary data");
    }

    return response.json();
  }

  static async createProject(): Promise<CreateProjectResponse> {
    const response = await aiApi.post(`/project/create`);
    if (response.status != 200) {
      throw new Error("unable to create project");
    }

    return response.data;
  }

  static async saveContent(project_id: string, content: string) {
    const data = {
      content,
      project_id,
    };

    const response = await aiApi.patch("/project", data);

    return response.data;
  }

  static async getAudioTranscript(formData: FormData) {
    const response = await aiApi.post(
      "/project/data/audio-transcription",
      formData
    );

    return response.data;
  }

  static async createChatSession(
    project_id: string,
    chat_type: "inline_chat" | "sidebar_chat",
    focus_chunk = ""
  ): Promise<{ session_id: string }> {
    const reqBody = {
      project_id,
      chat_type,
      focus_chunk,
    };

    if (chat_type === "inline_chat" && focus_chunk === "") {
      throw new Error("focus chunk cannot be empty");
    }

    const response = await aiApi.post(
      "/assistant/chat/session/create",
      reqBody
    );

    return response.data;
  }

  static async closeChatSession(
    user_id: string,
    project_id: string,
    chat_session_id: string
  ) {
    const reqBody = {
      user_id,
      project_id,
      chat_session_id,
    };

    const response = await aiApi.post("/assistant/chat/session/close", reqBody);

    return response.data;
  }

  // this is temporary while i await smart to stream
  static async sendMessage(
    type: "inline" | "sidebar",
    payload: FormData
  ): Promise<{ assistant_response: string; session_id: string }> {
    const response = await aiApi.post(
      `/assistant/${type}/chat/message/send`,
      payload
    );

    return response.data;
  }

  static async getProjectContext(projectId: string): Promise<string[]> {
    const response = await aiApi.get(`/project/data/contexts/${projectId}`);
    return response.data;
  }

  static async subscribe(lookup_key: string): Promise<{ url: string }> {
    const response = await authApi.post("/stripe/create-checkout-session", {
      lookup_key,
    });
    return response.data.data;
  }

  static async getProducts(): Promise<Product[]> {
    const response = await authApi.get("/stripe/products");
    return response.data.data;
  }

  static async getUser(): Promise<User> {
    const response = await authApi.get("/user/me");
    return response.data.data;
  }

  static async billing(): Promise<{ url: string }> {
    const response = await authApi.get("/stripe/billing");
    return response.data.data;
  }

  static async deleteAccount() {
    const response = await authApi.delete("/user/me");
    return response.data.data;
  }

  // static async fetchProjectsSupa({
  //   pageParam = 0,
  //   searchQuery = "",
  //   limit = 10,
  //   user_id,
  // }: {
  //   pageParam?: number | null;
  //   user_id: string;
  //   searchQuery?: string;
  //   limit?: number;
  // }): Promise<FetchProjectsResponseSupa> {
  //   let query = supabase
  //     .from("project")
  //     .select(
  //       "id, name, created_at, updated_at, current_content_updated_at, base_content, target_length"
  //     )

  //     .eq("user_id", user_id)
  //     .order("created_at", { ascending: false })
  //     .range(pageParam as number, (pageParam as number) + limit - 1);

  //   if (searchQuery) {
  //     query = query.ilike("name", `%${searchQuery}%`);
  //   }

  //   const { data, error } = await query;

  //   if (error) {
  //     throw new Error(error.message);
  //   }

  //   const hasMore = data.length === limit;

  //   return {
  //     projects: data || [],
  //     nextCursor: hasMore ? (pageParam as number) + limit : null,
  //   };
  // }

  static async hasWritingPatterns(): Promise<boolean> {
    const response = await authApi.get("/user/writing-pattern");
    return response.data.data;
  }

  static async shareProject(
    projectId: string,
    visibility?: Visibility
  ): Promise<SharedProject> {
    const response = await authApi.post(`/shared/create/${projectId}`, {
      visibility,
    });
    return response.data.data;
  }

  static async updateSharedProject(
    projectId: string,
    params: { visibility?: Visibility; update_content?: boolean }
  ) {
    const response = await authApi.put(`/shared/${projectId}`, params);
    return response.data.data;
  }

  static async getSharedProject(
    sharedProjectId: string
  ): Promise<SharedProjectViewer> {
    const token = getAuthTokens()?.token;

    const response = await axios.get(
      `${SERVER_API_PUBLIC}/shared/${sharedProjectId}`,
      {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {}, // no auth header
      }
    );

    return response.data.data;
  }

  static async getSharedProjectOwner(
    projectId: string
  ): Promise<SharedProject> {
    const response = await authApi.get(`/shared/owner/${projectId}`);
    return response.data.data;
  }

  /**
   * Used for saving context sources & save for later functionality
   * @param projectId
   */
  static async saveDocument(data: FormValues) {
    const formData = prepareFormData(data);
    const response = await aiApi.post(
      "/project/data/contexts/analyse",
      formData
    );
    return response.data;
  }

  static async getUsageStats(): Promise<UsageStats> {
    const response = await aiApi.get("/user/usage");
    return response.data;
  }

  static async getChatSessions({
    projectId,
    pageParam = 0,
    limit = 10,
  }: {
    projectId: string;
    pageParam?: number | null;
    limit?: number;
  }): Promise<{
    sessions: ChatSession[];
    nextCursor: number | null;
  }> {
    const page = (pageParam ?? 0) / limit + 1;

    const response = await aiApi.get(
      `/${projectId}/chat-sessions?page=${page}&page_size=${limit}`
    );

    const { sessions, current_page, total_pages } = response.data;

    const hasMore = current_page < total_pages;

    return {
      sessions,
      nextCursor: hasMore ? (pageParam ?? 0) + limit : null,
    };
  }
  static async getChatSessionMessages(
    projectId: string,
    chat_session_id: string
  ): Promise<ChatMessage[]> {
    const response = await aiApi.get(
      `/${projectId}/chat-sessions/${chat_session_id}/messages`
    );
    return response.data;
  }
  static async getChatSessionData(
    projectId: string,
    chat_session_id: string
  ): Promise<ChatSession> {
    const response = await aiApi.get(
      `/${projectId}/chat-sessions/${chat_session_id}`
    );
    return response.data;
  }

  static async getChatSessionSuggestedName(
    projectId: string,
    chat_session_id: string
  ) {
    const response = await aiApi.get(
      `/${projectId}/chat/suggested-name?chat_session_id=${chat_session_id}`
    );
    return response.data;
  }

  static async getSuggestedProjectName(
    projectId: string
  ): Promise<{ name: string }> {
    const response = await aiApi.get(
      `/project/suggested-name?project_id=${projectId}`
    );
    return response.data;
  }

  static async contactSupport(payload: {
    name: string;
    email: string;
    subject?: string;
    message: string;
  }): Promise<boolean> {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/public/contact`,
      payload
    );
    return response.data.data;
  }

  static async signup(payload: {
    name: string;
    email: string;
    password: string;
  }): Promise<AuthResponse> {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/register`,
      payload
    );
    return response.data.data;
  }

  static async login(payload: {
    email: string;
    password: string;
  }): Promise<AuthResponse> {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/login`,
      payload
    );
    return response.data.data;
  }

  static async loginAdmin(payload: {
    email: string;
    password: string;
  }): Promise<AuthResponse> {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/admin/login`,
      payload
    );
    return response.data.data;
  }

  static async logout(): Promise<AuthResponse> {
    const response = await axios.post(`${SERVER_API_PUBLIC}/auth/logout`);
    return response.data.data;
  }

  static async forgotPassword(email: string, captchaToken: string) {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/forgot-password`,
      { email, captchaToken }
    );
    return response.data.data;
  }

  static async validateToken(token: string) {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/validate-reset-token`,
      { token }
    );
    return response.data.data;
  }
  static async resetPassword(token: string, password: string) {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/reset-password`,
      { token, password }
    );
    return response.data.data;
  }

  static async verifyOTP(
    otp: string,
    email: string
  ): Promise<{
    is_email_verified: boolean;
    user_profile: {
      is_premium: boolean;
      project: Pick<Project, "id" | "name" | "created_at" | "updated_at">[];
    };
  }> {
    const response = await axios.post(`${SERVER_API_PUBLIC}/auth/verify-otp`, {
      otp,
      email,
    });

    return response.data.data;
  }

  static async sendOTP(email: string) {
    const response = await axios.post(`${SERVER_API_PUBLIC}/auth/send-otp`, {
      email,
    });

    return response.data.data;
  }

  static async verifyGoogleSigninToken(
    token: string
  ): Promise<{ newlyVerified: boolean; user: SignelUserResponse }> {
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/verify-google`,
      null,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.data;
  }

  static async refreshToken(): Promise<{ token: string }> {
    const authTokens = getAuthTokens();
    const response = await axios.post(
      `${SERVER_API_PUBLIC}/auth/refresh`,
      {
        token: authTokens?.token,
      },
      {
        headers: {
          Authorization: `Bearer ${authTokens?.refreshToken}`,
        },
      }
    );
    return response.data.data;
  }

  static async updateProfile(
    update: UpdateUserInput
  ): Promise<{ name: string; email: string }> {
    const response = await authApi.patch("/user/me", update);
    return response.data.data;
  }

  static async uploadImage(form: FormData): Promise<{
    url: string;
    public_id: string;
  }> {
    const response = await authApi.post("/user/upload/image", form, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.data;
  }

  static async deleteImage(publicId: string): Promise<void> {
    const response = await authApi.delete(`/user/upload/image/${publicId}`);
    return response.data.data;
  }

  static async validateLink(link: string): Promise<{
    accessibility: Record<string, boolean>;
  }> {
    const links = [link];
    const response = await aiApi.post(`/project/data/contexts/links/validate`, {
      links,
    });
    return response.data;
  }

  static async getDashboardCounts(): Promise<DashboardCountMetrics> {
    const response = await authApi.get("/admin/metrics-count");
    return response.data.data;
  }

  static async getDashboardChartData(): Promise<DashboardChartsData> {
    const response = await authApi.get("/admin/chart-data");
    return response.data.data;
  }
}
